import $ from 'jquery';
import { Modal } from 'bootstrap';
import { utils } from '../common/utils';
import type { IHonoraryInfo } from '../types';

export class DonationGiftForm {
    private $donationFlow: JQuery<HTMLElement>;
    private $honoraryFirstName: JQuery<HTMLElement>;
    private $honoraryLastName: JQuery<HTMLElement>;
    private $honoraryGreeting: JQuery<HTMLElement>;
    private $honorarySignature: JQuery<HTMLElement>;
    private $imagefilecontainer: JQuery<HTMLElement>;
    private $userimage: JQuery<HTMLElement>;
    private $honoraryDate: JQuery<HTMLElement>;
    private $preview: JQuery<HTMLElement>;
    private $certificatepreview: JQuery<HTMLElement>;
    private $progressbar: JQuery<HTMLElement>;
    private imagetype: string = '';
    private standardImageRef: string = '';
    private customImageRef: string = '';
    private runningRequest: JQuery.jqXHR | null = null;

    constructor(element: HTMLElement | JQuery<HTMLElement>) {
        this.$donationFlow = utils.isJquery(element) ? element : $(element);
        this.$honoraryFirstName = this.$donationFlow.find('.giftform .hpfirstname');
        this.$honoraryLastName = this.$donationFlow.find('.giftform .hplastname');
        this.$honoraryGreeting = this.$donationFlow.find('.giftform .giftform-greeting');
        this.$honorarySignature = this.$donationFlow.find('.giftform .giftform-signature');
        this.$imagefilecontainer = this.$donationFlow.find('.imagefilecontainer');
        this.$userimage = this.$imagefilecontainer.find('input[type=file]');
        this.$honoraryDate = this.$donationFlow.find('.giftform .honorarydate');
        this.$preview = this.$donationFlow.find('.preview');
        this.$certificatepreview = this.$preview.closest('.certificatepreview');
        this.$progressbar = this.$donationFlow.find('.progessbar');
        this.setInitialImageValue();
        this.initGiftFormEvents();
    }

    get HonoraryInfo(): IHonoraryInfo {
        let utcDateString: string | null = null;
        const localDateString = this.$honoraryDate.val() as string;
        if (localDateString) {
            const localDate = new Date(localDateString);
            utcDateString = localDate.toISOString();
        }

        return {
            honoraryFirstName: this.$honoraryFirstName.val(),
            honoraryLastName: this.$honoraryLastName.val(),
            honoraryMessage: this.$honoraryGreeting.val(),
            honoraryMessageFrom: this.$honorarySignature.val(),
            honoraryDateUtc: utcDateString,
            imageType: this.imagetype,
            imageReference: this.imagetype === 'standard' ? this.standardImageRef : this.customImageRef
        };
    }

    isValid(): boolean {
        let result = true;

        if (this.$honoraryFirstName.val() === '') {
            this.$honoraryFirstName.addClass('is-invalid');
            result = false;
        } else {
            this.$honoraryFirstName.removeClass('is-invalid');
        }

        if (this.$honoraryLastName.val() === '') {
            this.$honoraryLastName.addClass('is-invalid');
            result = false;
        } else {
            this.$honoraryLastName.removeClass('is-invalid');
        }

        const checkedStandardImage = this.$imagefilecontainer.find('input[type=radio]:checked');
        if (checkedStandardImage.length === 0 && this.$userimage.val() === '') {
            this.$imagefilecontainer.addClass('is-invalid');
            result = false;
        } else {
            this.$imagefilecontainer.removeClass('is-invalid');
        }

        return result;
    }

    private dispatchRenderPreviewEvent(): void {
        const currentImageType = this.imagetype || 'standard';
        this.$donationFlow[0].dispatchEvent(new CustomEvent('renderpreview', {
            detail: { imagetype: currentImageType, stdImageRef: this.standardImageRef, customImageRef: this.customImageRef }
        }));
    }

    private updateProgessBar(currentProgress: number): void {
        if (currentProgress === 0) {
            this.$progressbar.removeAttr('style');
        } else {
            this.$progressbar.css('width', currentProgress + '%');
        }
        this.$progressbar.prop('aria-valuenow', currentProgress);
    }

    private setInitialImageValue(): void {
        const standardImages = this.$imagefilecontainer.find('input[type=radio]');
        if (standardImages.length > 0) {
            this.standardImageRef = (standardImages[0] as HTMLInputElement).value;
            this.imagetype = 'standard';
            $(standardImages[0]).trigger('click');
        }
    }

    private initGiftFormEvents(): void {
        const standardImages = this.$imagefilecontainer.find('input[type=radio]');

        standardImages.on('click', (evt) => {
            this.$userimage.val('');
            const filelabel = this.$userimage.prev('label');
            filelabel.text(filelabel.data('default-text') as string);
            this.imagetype = 'standard';
            this.standardImageRef = (evt.target as HTMLInputElement).value;
            this.dispatchRenderPreviewEvent();
        });

        this.$userimage.on('change', (evt) => {
            const fileinput = evt.target as HTMLInputElement;
            this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerrorclear'));
            const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
            const file = fileinput.files?.[0];
            if (!file) return;

            if (allowedTypes.includes(file.type)) {
                const imageapiurl = '/api/donations/userimage';
                const formData = new FormData();
                formData.append('file', file, utils.getFileNameFromPath(this.$userimage.val() as string));

                $.ajax({
                    xhr: () => {
                        const xhr = new window.XMLHttpRequest();
                        xhr.upload.addEventListener('progress', (progressEvt) => {
                            if (progressEvt.lengthComputable) {
                                this.updateProgessBar((progressEvt.loaded / progressEvt.total) * 100);
                            }
                        }, false);
                        return xhr;
                    },
                    type: 'POST',
                    url: imageapiurl,
                    data: formData,
                    contentType: false,
                    cache: false,
                    processData: false,
                    beforeSend: () => { this.updateProgessBar(0); },
                    error: () => {
                        this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerror', { detail: { error: 'Ett fel uppstod med filuppladdningen, försök igen.' } }));
                    },
                    success: (resp: { imageReference: string }) => {
                        this.updateProgessBar(100);
                        standardImages.prop('checked', false);
                        this.imagetype = 'custom';
                        this.customImageRef = resp.imageReference;
                        this.dispatchRenderPreviewEvent();
                    }
                });
            } else {
                this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerror', { detail: { error: 'Du kan bara ladda upp bilder i format jpeg eller png.' } }));
            }
        });

        this.$donationFlow.find('.giftform input, .giftform textarea').on('change', () => {
            this.dispatchRenderPreviewEvent();
        });

        // Clicking a pre-made verse in the verse modal fills the greeting textarea and closes
        // the modal. Verses may contain HTML (e.g. <br> line breaks), so normalise to plain
        // text with line breaks preserved, then trigger change so the preview re-renders.
        this.$donationFlow.on('click', '.js-verse-selector', (evt) => {
            evt.preventDefault();
            const normalisedHtml = $(evt.currentTarget).html()
                .replace(/<\s*br\s*\/?>/gi, '\n')
                .replace(/<\/\s*p\s*>\s*<\s*p[^>]*>/gi, '\n\n');
            const verseText = $('<div/>').html(normalisedHtml).text().trim();
            this.$honoraryGreeting.val(verseText).trigger('change');

            const modalEl = this.$donationFlow.find('#verseModal')[0];
            if (modalEl) Modal.getOrCreateInstance(modalEl).hide();
        });

        this.$donationFlow.on('redraw', () => {
            this.dispatchRenderPreviewEvent();
        });

        this.$donationFlow.on('renderpreview', () => {
            this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerrorclear'));
            this.$certificatepreview.addClass('rendering');

            const data = {
                replacements: {
                    'honorary-fullname': `${this.$honoraryFirstName.val() as string} ${this.$honoraryLastName.val() as string}`,
                    'honorary-message-from': this.$honorarySignature.val(),
                    'honorary-message': this.$honoraryGreeting.val()
                },
                useCustomImage: this.imagetype === 'custom',
                standardImageRef: this.standardImageRef,
                customImageRef: this.customImageRef,
                isMemoryGift: (this.$donationFlow[0] as HTMLElement & { isMemoryGift?: boolean }).isMemoryGift
            };

            this.runningRequest = $.ajax('/api/donations/preview', {
                beforeSend: () => {
                    if (this.runningRequest !== null) this.runningRequest.abort();
                },
                method: 'POST',
                data: JSON.stringify(data),
                contentType: 'application/json; charset=utf-8'
            })
                .fail((resp: JQuery.jqXHR) => {
                    if (resp.readyState === 4) {
                        this.$donationFlow[0].dispatchEvent(new CustomEvent('donationerror', { detail: { error: 'Det gick inte att skapa en förhandsgransking av din gåva.' } }));
                        this.$certificatepreview.removeClass('rendering');
                    }
                })
                .done((resp: { imageDataUri: string }) => {
                    this.$preview.prop('src', resp.imageDataUri);
                    this.$certificatepreview.removeClass('rendering');
                })
                .always(() => {
                    this.runningRequest = null;
                });
        });
    }
}
